package com.swetdot.swetgram.ui.profile

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.swetdot.swetgram.data.notifications.NotificationSettingsRepository
import com.swetdot.swetgram.domain.NotificationPreferences
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

data class NotificationSettingsUiState(
    val preferences: NotificationPreferences? = null,
    val loadedOnce: Boolean = false,
    val saving: Boolean = false,
    val error: String? = null,
)

class NotificationSettingsViewModel(
    private val notifications: NotificationSettingsRepository,
) : ViewModel() {
    private val _state = MutableStateFlow(NotificationSettingsUiState())
    val state: StateFlow<NotificationSettingsUiState> = _state.asStateFlow()

    init { refresh() }

    fun refresh() {
        viewModelScope.launch {
            try {
                _state.value = _state.value.copy(
                    preferences = notifications.get(),
                    loadedOnce = true,
                    error = null,
                )
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                _state.value = _state.value.copy(
                    loadedOnce = true,
                    error = error.profileMessage("Could not load notification settings."),
                )
            }
        }
    }

    fun update(next: NotificationPreferences) {
        val previous = _state.value.preferences ?: return
        if (_state.value.saving) return
        _state.value = _state.value.copy(preferences = next, saving = true, error = null)
        viewModelScope.launch {
            try {
                _state.value = _state.value.copy(
                    preferences = notifications.update(next),
                    saving = false,
                )
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                _state.value = _state.value.copy(
                    preferences = previous,
                    saving = false,
                    error = error.profileMessage("Could not save notification settings."),
                )
            }
        }
    }

    fun dismissError() {
        _state.value = _state.value.copy(error = null)
    }
}
